import { FC, useEffect, useRef, useState } from "react";
import { Button, Flex, Input, TableProps, Tooltip, Modal, Tag } from "antd";
import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef,
} from "@/styles/STableLayout";
import { useTranslation } from "react-i18next";
import useDataList from "@/hooks/useDataList";
import { CommonListParams } from "@/types/common";
import apis from "@/apis/supplier";
import { PurchaseOrderItem, PurchaseOrder } from "@/types/purchase";
import { getStorage } from "@/utils/storage";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import SText from "@/styles/SText";
import { dateFormat, priceFormat } from "@/utils/format";
import dayjs from "dayjs";
import { CurrencySelect } from "@/components/Selector";
import { DictEnum } from "@/types/dict";
import Dict from "@/components/Dict";
import { ExclamationCircleOutlined } from '@ant-design/icons';
import PurchaseDetail from '@/pages/Purchase/pages/Order/components/PurchaseDetail'
import { resolve } from "path";
import { hasPermission } from "@/utils/user";
import { User, UserPermissionEnum } from "@/types/user";
import { RootState } from "@/store";
import { useSelector } from "react-redux";
const { confirm } = Modal;
const SupplierOrders: FC = () => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const [list, total, listLoading, { fetchList }] = useDataList<PurchaseOrderItem, [string, CommonListParams<string>?]>({ fetchApi: apis.supplierPurchaseItems });
  const [keyword, setKeyword] = useState<string>("");
  const [showDetail, setShowDetail] = useState<any>(null)
  const curUser: User = useSelector((state: RootState) => state.userReducer.curUser);


  useEffect(() => {
    fetch("", 0);
  }, []);

  const showConfirm = (action: number, id: string, state: number) => {
    confirm({
      title: t("common.tip"),
      content: action === 1 ? t("purchase.comfi") : t("purchase.rejectComfi"),
      icon: <ExclamationCircleOutlined />,
      okText: t("purchase.ok"),
      cancelText: t("purchase.cancel"),
      className: 'comfim-div',
      cancelButtonProps: { style: { color: 'black', fontSize: 14, height: 24 } },
      okButtonProps: { style: { color: 'white', fontSize: 14, height: 24, background: '#7a294f' } },
      onOk() {
        setShowDetail(null)
        apis.acceptStateUpdate(id, state).then(res => {
          fetch("", 0);
        })
      },
    });
  }

  const columns: TableProps<PurchaseOrderItem>["columns"] = [
    {
      title: <SText text={t("purchase.orderNumber")} />,
      dataIndex: "number",
      width: 140,
      fixed: "left",
      render: (_, item) => (
        <SText text={item?.number} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.count")} />,
      dataIndex: "count",
      width: 80,
      render: (_, item) => (
        <SText text={item?.count?.toString() ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.amount")} />,
      dataIndex: "amount",
      width: 80,
      render: (_, item) => (
        <SText text={priceFormat(item?.amount)} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.rateAmount")} />,
      dataIndex: "rateAmount",
      width: 80,
      render: (_, item) => (
        <SText text={priceFormat(item?.rateAmount)} style={{ width: "100%" }} />
      ),
    },
    {
      title: t("purchase.productInfo"),
      children: [
        {
          title: <SText text={t("product.code")} />,
          dataIndex: "code",
          width: 100,
          render: (_, item) => (
            <SText text={item?.product?.code ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("product.name")} />,
          dataIndex: "name",
          width: 100,
          render: (_, item) => (
            <SText text={item?.product?.name ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("product.brand1")} />,
          dataIndex: "model1",
          width: 120,
          render: (_, item) => (
            <SText
              text={item?.product?.model ?? ""}
              style={{ width: "100%" }}
            />
          ),
        },
      ],
    },
    {
      title: <SText text={t("purchase.orderTime")} />,
      dataIndex: "orderTime",
      width: 100,
      render: (_, item) =>
        item.orderTime ? dateFormat(dayjs(item.orderTime)) : "",
    },
    {
      title: <SText text={t("purchase.deliveredQuantity")} />,
      dataIndex: "deliveredQuantity",
      width: 80,
      render: (_, item) => item.deliveredQuantity,
    },
    {
      title: <SText text={t("purchase.currency")} />,
      dataIndex: "currency",
      width: 100,
      render: (_, item) => (
        <CurrencySelect value={item?.order?.currency} readOnly />
      ),
    },
    {
      title: <SText text={t("purchase.payMethod")} />,
      dataIndex: "payMethod",
      width: 120,
      render: (_, item) => (
        <Dict
          key={"payMethod"}
          type={DictEnum.PURCHASE_PAY_METHOD}
          value={item?.order?.payMethod}
          readOnly
        />
      ),
    },
    {
      title: <SText text={t("purchase.state")} />,
      dataIndex: "state",
      width: 80,
      fixed: "right",
      render: (_, item) => (
        <div>
          {item.order?.acceptState === null ? (
            <div>
              <Tag color="blue">{t("purchase.pend")}</Tag>
            </div>
          ) : item.order?.acceptState == 1 ? (
            <Tag color="green">{t("purchase.acceptType")}</Tag>
          ) : (
            <Tag color="red">{t("purchase.rejectType")}</Tag>
          )}
        </div>
      ),
    },
    {
      title: <SText text={t("purchase.operate")} />,
      dataIndex: "operate",
      width: 120,
      fixed: "right",
      render: (_, item) => (
        <div>
          <a onClick={() => {
            setShowDetail(item)
          }}>{t("common.detail")}</a>
          {/* {item.order?.acceptState === null ? (
            <div>
                <a onClick={()=> showConfirm(1, item.order.id, 1)}>{t("purchase.accept")}</a>
                <a onClick={()=> showConfirm(0, item.order.id, 0)} style={{marginLeft: 10}}>{t("purchase.reject")}</a>
            </div>
          ) : item.order?.acceptState == 1 ? (
              <span></span>
          ):(
              <a onClick={()=> setShowDetail(item.order)}>{t("purchase.rejectDetail")}</a>
          )} */}
        </div>
      ),
    }
  ];


  const fetch = (keyword: string, offset: number) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    fetchList(getStorage("SUPPLIER_ID"), {
      body: keyword,
      pagination: { offset, take },
    }).catch((err) => {
      switch (err) {
        case 10010:
          break;
        default:
          break;
      }
    });
  };

  const headerChildren = (
    <Flex style={{ width: "100%", marginBottom: 20 }} justify="end">
      <Tooltip title={t("supplierLayout.searchByKeyword")}>
        <Input.Search
          placeholder={t("supplierLayout.searchByKeyword")}
          style={{ width: 200 }}
          value={keyword}
          enterButton
          onChange={(e) => setKeyword(e.target.value.trim())}
          onSearch={(e) => {
            fetch(e, 0);
            tableRef?.current?.resetTablePage();
          }}
        />
      </Tooltip>
    </Flex>
  );

  const btnList = () => (
    <div style={{ position: 'absolute', bottom: 10, right: 51 }}>
      {
        showDetail && showDetail.order.acceptState === null ? (
          <>
            <Button onClick={() => showConfirm(0, showDetail.order.id, 0)} style={{ marginRight: 20 }}>{t("purchase.reject")}</Button>
            <Button onClick={() => showConfirm(1, showDetail.order.id, 1)} type={'primary'}>{t("purchase.accept")}</Button>
          </>

        ) : null
      }

    </div>
  )

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        loading={listLoading}
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={1400}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={(offset) => fetch(keyword, offset)}
      />
      {
        !!showDetail && (
          <PurchaseDetail canEdit={hasPermission(curUser, [UserPermissionEnum.PUR_ORDER_EDIT])}
            key={showDetail?.id}
            handleChangeStatus={() => {
              // rmItem(detail?.id);

            }}
            handleOk={(data, item, itemId) => {
              return new Promise(resolve => { })
            }}
            open={!!showDetail}
            confirmLoading={true}
            handleCancel={() => setShowDetail(null)}
            orderItemId={showDetail.id}
            isSupplier={true}
            order={showDetail}
            btnList={btnList}
          />
        )
      }
      {/* <Modal
        title={t("purchase.rejectInformate")}
        open={!!showDetail}
        onCancel={()=> setShowDetail(null)}
        footer={null}
      >
      <div>
          <div>
            <p>{t("purchase.state")}: {showDetail?.acceptState === 0 ? <Tag color="red">{t("purchase.rejectType")}</Tag> : ''}</p>
            <p>{t("purchase.rejectDate")}: {dateFormat(dayjs(showDetail?.acceptedAt))}</p>
            <p>{t("purchase.operationPerson")}: {showDetail?.acceptor.name1 ? showDetail?.acceptor.name1 : showDetail?.acceptor.name2 ? showDetail?.acceptor.name1 : showDetail?.acceptor.mobile}</p>
          </div>
      </div> 

      </Modal> */}
    </Flex>
  );
};

export default SupplierOrders;
